import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { differenceInYears, parseISO } from 'date-fns';
import { Plus } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { useApp } from '@/contexts/AppContext';
import { getNextReminderDueDate } from '@/lib/reminder-recurrence';
import HealthReminderForm from './HealthReminderForm';
import HealthWomensHealthSection from './HealthWomensHealthSection';
import HealthMedicationForm from './HealthMedicationForm';
import PetCareLogForm from './PetCareLogForm';
import HealthPeopleAndPetsSection from './HealthPeopleAndPetsSection';
import SectionAccordionCard from '@/components/SectionAccordionCard';
import { buildHealthSubjects, normalizeHealthSubjectRecord } from '../../../ui-foundation/features/health';
import { buildPetCareTemplates, type PetCareTemplateKey } from '../../../ui-foundation/features/pets';
import { buildSummary, type RecurrenceData } from '@/components/RecurrenceBuilder';
import {
  AppButton,
  AppButtonRow,
  AppEmptyState,
  AppInlineAlert,
  AppLoadingState,
  AppPanelButton,
} from '../../../ui-foundation';
import { TYPE_ICONS, type HealthReminder, type ReminderType } from './health-shared';

type AdultDependantRow = Database['public']['Tables']['adult_dependants']['Row'];
type HealthMedicationRow = Database['public']['Tables']['health_medications']['Row'];
type PetRow = Database['public']['Tables']['pets']['Row'];

interface ReminderSuggestion {
  type: ReminderType;
  title: string;
  body: string;
  notes?: string;
  cadenceLabel?: string;
  recurrence?: RecurrenceData | null;
}

type MyHealthSectionKey = 'suggested-reminders' | 'people-pets' | 'mens-health' | 'womens-health';

const MY_HEALTH_SECTION_EMOJI: Record<MyHealthSectionKey, string> = {
  'suggested-reminders': '⏰',
  'people-pets': '👨‍👩‍👧‍👦',
  'mens-health': '🧔',
  'womens-health': '🌸',
};

function MyHealthSection({
  emoji,
  title,
  subtitle: _subtitle,
  open,
  onToggle,
  children,
}: {
  emoji: string;
  title: string;
  subtitle: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <SectionAccordionCard emoji={emoji} title={title} subtitle={_subtitle} open={open} onToggle={onToggle}>
      <div className="space-y-4">{children}</div>
    </SectionAccordionCard>
  );
}

function notifyAutomationRefresh() {
  window.dispatchEvent(new CustomEvent('adulting:data-changed'));
}

export default function MyHealthTab({
  launchAction,
  onLaunchHandled,
}: {
  launchAction?: 'reminder' | 'medication' | 'pet-care' | null;
  onLaunchHandled?: () => void;
}) {
  const navigate = useNavigate();
  const { user, data } = useApp();
  const [reminders, setReminders] = useState<HealthReminder[]>([]);
  const [medications, setMedications] = useState<HealthMedicationRow[]>([]);
  const [adultDependants, setAdultDependants] = useState<AdultDependantRow[]>([]);
  const [pets, setPets] = useState<PetRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editingReminder, setEditingReminder] = useState<HealthReminder | null>(null);
  const [draftReminder, setDraftReminder] = useState<{
    type: ReminderType;
    title: string;
    notes?: string;
    cadenceLabel?: string;
    recurrence?: RecurrenceData | null;
    subjectValue?: string;
  } | null>(null);
  const [medicationFormOpen, setMedicationFormOpen] = useState(false);
  const [editingMedication, setEditingMedication] = useState<HealthMedicationRow | null>(null);
  const [draftMedicationSubjectValue, setDraftMedicationSubjectValue] = useState<string | undefined>(undefined);
  const [petCareLogSubjectValue, setPetCareLogSubjectValue] = useState<string | null>(null);
  const [openSections, setOpenSections] = useState<Record<MyHealthSectionKey, boolean>>({
    'suggested-reminders': false,
    'people-pets': false,
    'mens-health': false,
    'womens-health': false,
  });

  const fetchHealthData = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    const [
      { data: reminderRows },
      { data: medicationRows },
      { data: adultRows },
      { data: petRows },
    ] = await Promise.all([
      supabase
        .from('health_reminders')
        .select('*')
        .eq('user_id', user.id)
        .order('due_date', { ascending: true, nullsFirst: false })
        .order('created_at', { ascending: false }),
      supabase
        .from('health_medications')
        .select('*')
        .eq('user_id', user.id)
        .order('repeat_due_date', { ascending: true, nullsFirst: false })
        .order('medication_name', { ascending: true }),
      supabase
        .from('adult_dependants')
        .select('*')
        .eq('user_id', user.id)
        .order('name', { ascending: true }),
      supabase
        .from('pets')
        .select('*')
        .order('name', { ascending: true }),
    ]);

    setReminders((reminderRows as HealthReminder[]) || []);
    setMedications((medicationRows as HealthMedicationRow[]) || []);
    setAdultDependants((adultRows as AdultDependantRow[]) || []);
    setPets((petRows as PetRow[]) || []);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    void fetchHealthData();

    const handleRefresh = () => {
      void fetchHealthData();
    };

    window.addEventListener('adulting:data-changed', handleRefresh);
    return () => window.removeEventListener('adulting:data-changed', handleRefresh);
  }, [fetchHealthData]);

  const age = useMemo(() => {
    if (!data.profile.dateOfBirth) return null;
    const dob = parseISO(data.profile.dateOfBirth);
    if (Number.isNaN(dob.getTime())) return null;
    return differenceInYears(new Date(), dob);
  }, [data.profile.dateOfBirth]);

  const hasHealthProfile = useMemo(() => {
    const profile = data.profile;
    return Boolean(
      profile.dateOfBirth ||
      profile.screeningInvitationPath ||
      profile.hasCervix !== null ||
      profile.breastScreeningRelevant !== null ||
      profile.prostateScreeningRelevant !== null ||
      profile.onHrt
    );
  }, [data.profile]);

  const showMensHealthSection = useMemo(
    () => data.profile.screeningInvitationPath === 'male' || data.profile.prostateScreeningRelevant === true,
    [data.profile.prostateScreeningRelevant, data.profile.screeningInvitationPath],
  );

  const selfReminders = useMemo(
    () =>
      reminders.filter((reminder) => {
        if (reminder.completed_at) return false;
        return normalizeHealthSubjectRecord({
          subjectType: reminder.subject_type,
          subjectId: reminder.subject_id,
          subjectName: reminder.subject_name,
        }).subjectType === 'self';
      }),
    [reminders]
  );

  const profileSuggestions = useMemo(() => {
    const existingTypes = new Set(selfReminders.map((reminder) => reminder.type));
    const suggestions: ReminderSuggestion[] = [];

    if (!existingTypes.has('dentist')) {
      suggestions.push({
        type: 'dentist',
        title: 'Dental check-up reminder',
        body: 'A regular dental review is one of the easiest health-admin jobs to lose track of when life gets busy.',
        cadenceLabel: 'Prefills every 6 months',
        recurrence: {
          recurrence_type: 'custom',
          recurrence_interval: 6,
          recurrence_unit: 'months',
          recurrence_day_of_week: null,
          recurrence_week_of_month: null,
          recurrence_month_day: null,
        },
      });
    }

    if (!existingTypes.has('optician')) {
      suggestions.push({
        type: 'optician',
        title: 'Opticians check-up reminder',
        body: 'Useful for routine eye tests, contact lens reviews, or checking whether glasses need updating.',
        cadenceLabel: 'Prefills every 24 months',
        recurrence: {
          recurrence_type: 'custom',
          recurrence_interval: 24,
          recurrence_unit: 'months',
          recurrence_day_of_week: null,
          recurrence_week_of_month: null,
          recurrence_month_day: null,
        },
      });
    }

    if (data.profile.hasCervix === true && age !== null && age >= 25 && age <= 64 && !existingTypes.has('smear')) {
      suggestions.push({
        type: 'smear',
        title: 'Cervical screening reminder',
        body: 'Use your last invite or screening result date. England moved to more personalised recall from July 2025, so do not assume a blanket 3-year interval.',
        notes: 'Set this from your actual NHS invite or result date.',
        cadenceLabel: 'Needs actual NHS date',
        recurrence: null,
      });
    }

    if (data.profile.breastScreeningRelevant === true && age !== null && age >= 50 && !existingTypes.has('mammogram')) {
      suggestions.push({
        type: 'mammogram',
        title: 'Breast screening reminder',
        body: 'Useful if you are in the NHS breast screening age range or already receiving invites.',
        notes: 'Routine NHS invites are usually every 3 years once screening starts.',
        cadenceLabel: 'Prefills every 3 years',
        recurrence: {
          recurrence_type: 'custom',
          recurrence_interval: 36,
          recurrence_unit: 'months',
          recurrence_day_of_week: null,
          recurrence_week_of_month: null,
          recurrence_month_day: null,
        },
      });
    }

    if (age !== null && age >= 40 && age <= 74 && !existingTypes.has('health-check')) {
      suggestions.push({
        type: 'health-check',
        title: 'NHS Health Check reminder',
        body: 'This is a good fit if you want Adulting to prompt for the NHS Health Check cycle.',
        notes: 'Routine NHS Health Check invitations are generally every 5 years.',
        cadenceLabel: 'Prefills every 5 years',
        recurrence: {
          recurrence_type: 'custom',
          recurrence_interval: 60,
          recurrence_unit: 'months',
          recurrence_day_of_week: null,
          recurrence_week_of_month: null,
          recurrence_month_day: null,
        },
      });
    }

    if (data.profile.prostateScreeningRelevant === true && age !== null && age >= 50 && !existingTypes.has('prostate')) {
      suggestions.push({
        type: 'prostate',
        title: 'Prostate / PSA discussion reminder',
        body: 'There is no routine NHS screening programme for prostate cancer, so this works better as a GP discussion or PSA check reminder.',
        notes: 'Use this if a clinician has advised you to discuss PSA testing or prostate symptoms.',
        cadenceLabel: 'Manual timing',
        recurrence: null,
      });
    }

    if (data.profile.onHrt && !existingTypes.has('hrt-review')) {
      suggestions.push({
        type: 'hrt-review',
        title: 'HRT review reminder',
        body: 'Helpful for medication reviews and follow-up appointments if you are on HRT.',
        notes: 'Set this to your next planned review date or the date your GP asked you to come back.',
        cadenceLabel: 'Prefills annually',
        recurrence: {
          recurrence_type: 'custom',
          recurrence_interval: 12,
          recurrence_unit: 'months',
          recurrence_day_of_week: null,
          recurrence_week_of_month: null,
          recurrence_month_day: null,
        },
      });
    }

    return suggestions;
  }, [
    age,
    data.profile.breastScreeningRelevant,
    data.profile.hasCervix,
    data.profile.onHrt,
    data.profile.prostateScreeningRelevant,
    selfReminders,
  ]);

  const subjects = useMemo(() => buildHealthSubjects({
    partnerName: data.profile.partnerName,
    children: data.profile.children,
    adultDependants,
    pets: pets.map((pet) => ({ id: pet.id, name: pet.name, petType: pet.pet_type })),
    records: [
      ...reminders.map((item) => ({
        subjectType: item.subject_type,
        subjectId: item.subject_id,
        subjectName: item.subject_name,
      })),
      ...medications.map((item) => ({
        subjectType: item.subject_type,
        subjectId: item.subject_id,
        subjectName: item.subject_name || item.person_name,
      })),
    ],
  }), [adultDependants, data.profile.children, data.profile.partnerName, medications, pets, reminders]);

  const subjectOptions = useMemo(() => subjects.map((subject) => ({
    value: subject.key,
    label: `${subject.displayName} (${subject.subtitle})`,
    subjectType: subject.subjectType,
    subjectId: subject.subjectId,
    subjectName: subject.subjectName,
    subtitle: subject.subtitle,
  })), [subjects]);

  const handleComplete = async (reminder: HealthReminder) => {
    await supabase.from('health_reminders').update({ completed_at: new Date().toISOString() } as any).eq('id', reminder.id);
    const nextDate = getNextReminderDueDate({
      dueDate: reminder.due_date,
      frequencyMonths: reminder.frequency_months,
      recurrenceType: reminder.recurrence_type,
      recurrenceInterval: reminder.recurrence_interval,
      recurrenceUnit: reminder.recurrence_unit,
      recurrenceDayOfWeek: reminder.recurrence_day_of_week,
      recurrenceWeekOfMonth: reminder.recurrence_week_of_month,
      recurrenceMonthDay: reminder.recurrence_month_day,
    });

    if (nextDate) {
      await supabase.from('health_reminders').insert({
        user_id: reminder.user_id,
        type: reminder.type,
        title: reminder.title,
        due_date: nextDate,
        due_time: reminder.due_time || null,
        frequency_months: reminder.frequency_months,
        notes: reminder.notes,
        reminder_days_before: reminder.reminder_days_before ?? null,
        subject_type: reminder.subject_type || 'self',
        subject_id: reminder.subject_id || null,
        subject_name: reminder.subject_name || 'Me',
        recurrence_type: reminder.recurrence_type || 'one-off',
        recurrence_interval: reminder.recurrence_interval,
        recurrence_unit: reminder.recurrence_unit,
        recurrence_day_of_week: reminder.recurrence_day_of_week,
        recurrence_week_of_month: reminder.recurrence_week_of_month,
        recurrence_month_day: reminder.recurrence_month_day,
      } as any);
    }

    notifyAutomationRefresh();
    void fetchHealthData();
  };

  const handleDeleteReminder = async (id: string) => {
    await supabase.from('health_reminders').delete().eq('id', id);
    notifyAutomationRefresh();
    void fetchHealthData();
  };

  const handleDeleteMedication = async (item: HealthMedicationRow) => {
    if (item.reminder_id) {
      await supabase.from('health_reminders').delete().eq('id', item.reminder_id);
    }
    await supabase.from('health_medications').delete().eq('id', item.id);
    notifyAutomationRefresh();
    void fetchHealthData();
  };

  const handleSavedReminder = () => {
    setFormOpen(false);
    setEditingReminder(null);
    setDraftReminder(null);
    notifyAutomationRefresh();
    void fetchHealthData();
  };

  const handleSavedMedication = () => {
    setMedicationFormOpen(false);
    setEditingMedication(null);
    setDraftMedicationSubjectValue(undefined);
    notifyAutomationRefresh();
    void fetchHealthData();
  };

  const openEditReminder = (reminder: HealthReminder) => {
    setDraftReminder(null);
    setEditingReminder(reminder);
    setFormOpen(true);
  };

  const openAddReminder = (subjectKey?: string) => {
    setDraftReminder(subjectKey ? {
      type: 'gp',
      title: '',
      subjectValue: subjectKey,
    } : null);
    setEditingReminder(null);
    setFormOpen(true);
  };

  const openSuggestedReminder = (suggestion: ReminderSuggestion) => {
    setEditingReminder(null);
    setDraftReminder({
      type: suggestion.type,
      title: suggestion.title,
      notes: suggestion.notes || suggestion.body,
      cadenceLabel: suggestion.cadenceLabel,
      recurrence: suggestion.recurrence ?? null,
      subjectValue: 'self',
    });
    setFormOpen(true);
  };

  const openAddMedication = (subjectKey?: string) => {
    setEditingMedication(null);
    setDraftMedicationSubjectValue(subjectKey);
    setMedicationFormOpen(true);
  };

  const openPetCareTemplate = (subjectKey: string, petName: string, templateKey: PetCareTemplateKey) => {
    const template = buildPetCareTemplates(petName).find((item) => item.key === templateKey);
    if (!template) return;

    setEditingReminder(null);
    setDraftReminder({
      type: template.reminderType,
      title: template.title,
      notes: template.notes,
      reminderDaysBefore: template.reminderDaysBefore,
      recurrence: template.frequencyMonths
        ? {
            recurrence_type: 'custom',
            recurrence_interval: template.frequencyMonths,
            recurrence_unit: 'months',
            recurrence_day_of_week: null,
            recurrence_week_of_month: null,
            recurrence_month_day: null,
          }
        : null,
      subjectValue: subjectKey,
    });
    setFormOpen(true);
  };

  const openPetCareLog = (subjectKey: string) => {
    setPetCareLogSubjectValue(subjectKey);
  };

  const openEditMedication = (medication: HealthMedicationRow) => {
    setEditingMedication(medication);
    setDraftMedicationSubjectValue(undefined);
    setMedicationFormOpen(true);
  };

  useEffect(() => {
    if (!launchAction) {
      return;
    }

    if (launchAction === 'reminder') {
      openAddReminder('self');
      onLaunchHandled?.();
      return;
    }

    if (launchAction === 'medication') {
      openAddMedication('self');
      onLaunchHandled?.();
      return;
    }

    if (launchAction === 'pet-care') {
      const firstPetSubject = subjects.find((subject) => subject.subjectType === 'pet');

      if (!firstPetSubject) {
        onLaunchHandled?.();
        return;
      }

      openPetCareLog(firstPetSubject.key);
      onLaunchHandled?.();
    }
  }, [subjects, launchAction, onLaunchHandled, openAddMedication, openAddReminder, openPetCareLog]);

  const toggleSection = useCallback((section: MyHealthSectionKey) => {
    setOpenSections((current) => ({
      ...current,
      [section]: !current[section],
    }));
  }, []);

  if (loading) {
    return <AppLoadingState title="Loading health admin" body="Pulling people, pets, reminders, medications, and suggestions." />;
  }

  return (
    <div className="relative space-y-4">
      {!hasHealthProfile && (
        <AppInlineAlert title="Complete your health profile first">
          Adulting can suggest better reminders once it knows your age and which screening or review paths are actually relevant to you.
        </AppInlineAlert>
      )}

      {!hasHealthProfile && (
        <AppButtonRow>
          <AppButton size="sm" onPress={() => navigate('/profile')}>
            Edit profile & health
          </AppButton>
        </AppButtonRow>
      )}

      {profileSuggestions.length > 0 ? (
        <MyHealthSection
          emoji={MY_HEALTH_SECTION_EMOJI['suggested-reminders']}
          title="Suggested reminders"
          subtitle="Start with the health reminders Adulting can already see may apply."
          open={openSections['suggested-reminders']}
          onToggle={() => toggleSection('suggested-reminders')}
        >
          <div className="space-y-2">
            {profileSuggestions.map((suggestion) => (
              <AppPanelButton
                key={suggestion.type}
                onClick={() => openSuggestedReminder(suggestion)}
                className="items-start gap-3 bg-background p-4"
              >
                <span className="mt-0.5 shrink-0 text-xl">{TYPE_ICONS[suggestion.type]}</span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground">{suggestion.title}</p>
                  {suggestion.cadenceLabel ? (
                    <p className="mt-1 text-[11px] font-medium text-primary">{suggestion.cadenceLabel}</p>
                  ) : null}
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{suggestion.body}</p>
                </div>
                <Plus size={16} className="mt-0.5 shrink-0 text-primary" />
              </AppPanelButton>
            ))}
          </div>
        </MyHealthSection>
      ) : null}

      <MyHealthSection
        emoji={MY_HEALTH_SECTION_EMOJI['people-pets']}
        title="People and pets"
        subtitle="Keep appointments, medications, and pet care history grouped by who they belong to."
        open={openSections['people-pets']}
        onToggle={() => toggleSection('people-pets')}
      >
        <HealthPeopleAndPetsSection
          subjects={subjects}
          reminders={reminders}
          medications={medications}
          onAddReminder={(subject) => openAddReminder(subject.key)}
          onEditReminder={openEditReminder}
          onCompleteReminder={handleComplete}
          onDeleteReminder={handleDeleteReminder}
          onAddMedication={(subject) => openAddMedication(subject.key)}
          onEditMedication={openEditMedication}
          onDeleteMedication={handleDeleteMedication}
          onOpenPetCareTemplate={(subject, templateKey) => openPetCareTemplate(subject.key, subject.displayName, templateKey)}
          onAddPetCareLog={(subject) => openPetCareLog(subject.key)}
        />
      </MyHealthSection>

      {showMensHealthSection ? (
        <MyHealthSection
          emoji={MY_HEALTH_SECTION_EMOJI['mens-health']}
          title="Men&apos;s health"
          subtitle="Mental health and age-based checks kept in view too."
          open={openSections['mens-health']}
          onToggle={() => toggleSection('mens-health')}
        >
          <AppInlineAlert
            title="This includes mental health too"
            body="Low mood, numbness, irritability, burnout, or going quiet all count. Adulting keeps that visible in Wellbeing too, not just physical checks."
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-2xl border border-border bg-background px-4 py-3">
              <p className="text-sm font-semibold text-foreground">
                {age !== null && age >= 40 && age <= 74 ? 'NHS Health Check' : 'Routine GP checks'}
              </p>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                {age !== null && age >= 40 && age <= 74
                  ? 'Adulting can keep the 40 to 74 NHS Health Check cycle visible in reminders.'
                  : 'Keep blood pressure, symptoms, and anything changing visible in reminders before it drifts.'}
              </p>
            </div>
            {data.profile.prostateScreeningRelevant === true && age !== null && age >= 50 ? (
              <div className="rounded-2xl border border-border bg-background px-4 py-3">
                <p className="text-sm font-semibold text-foreground">Prostate / PSA discussion</p>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                  Adulting can keep a GP discussion reminder visible when prostate checks are relevant for you.
                </p>
              </div>
            ) : null}
            <div className="rounded-2xl border border-border bg-background px-4 py-3 sm:col-span-2">
              <p className="text-sm font-semibold text-foreground">Wellbeing still matters here</p>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                Use Wellbeing Check In and Support when today feels heavy if the load is showing up as stress, flatness, withdrawal, or burnout.
              </p>
            </div>
          </div>
        </MyHealthSection>
      ) : null}

      <MyHealthSection
        emoji={MY_HEALTH_SECTION_EMOJI['womens-health']}
        title="Women&apos;s health"
        subtitle="Cycle, smear, and contraception context kept private to Health."
        open={openSections['womens-health']}
        onToggle={() => toggleSection('womens-health')}
      >
        <HealthWomensHealthSection />
      </MyHealthSection>

      {subjects.length === 0 ? (
        <AppEmptyState
          title="No people or pets to organise yet"
          body="Add your first reminder or medication to start building the health view."
          action={(
            <AppButton onPress={() => openAddReminder('self')}>
              <Plus size={14} />
              Add reminder
            </AppButton>
          )}
        />
      ) : null}

      {formOpen ? (
        <HealthReminderForm
          onClose={() => {
            setFormOpen(false);
            setEditingReminder(null);
            setDraftReminder(null);
          }}
          onSaved={handleSavedReminder}
          editReminder={editingReminder}
          draftReminder={draftReminder}
          subjectOptions={subjectOptions}
        />
      ) : null}

      {petCareLogSubjectValue ? (
        <PetCareLogForm
          onClose={() => setPetCareLogSubjectValue(null)}
          onSaved={() => {
            setPetCareLogSubjectValue(null);
            notifyAutomationRefresh();
            void fetchHealthData();
          }}
          initialSubjectValue={petCareLogSubjectValue}
          subjectOptions={subjectOptions.filter((option) => option.subjectType === 'pet')}
        />
      ) : null}

      {medicationFormOpen ? (
        <HealthMedicationForm
          onClose={() => {
            setMedicationFormOpen(false);
            setEditingMedication(null);
            setDraftMedicationSubjectValue(undefined);
          }}
          onSaved={handleSavedMedication}
          editMedication={editingMedication}
          subjectOptions={subjectOptions}
          initialSubjectValue={draftMedicationSubjectValue}
        />
      ) : null}
    </div>
  );
}

export function getRecurrenceLabel(reminder: HealthReminder): string {
  if (reminder.recurrence_type === 'custom' && reminder.recurrence_interval && reminder.recurrence_unit) {
    return buildSummary({
      recurrence_type: 'custom',
      recurrence_interval: reminder.recurrence_interval,
      recurrence_unit: reminder.recurrence_unit as any,
      recurrence_day_of_week: reminder.recurrence_day_of_week || null,
      recurrence_week_of_month: reminder.recurrence_week_of_month || null,
      recurrence_month_day: reminder.recurrence_month_day || null,
    });
  }
  if (reminder.frequency_months) return `Every ${reminder.frequency_months}mo`;
  return '';
}
